import { EmptyGuid } from "../emptyGuid";
import { luscinia } from "../luscinia";
import { AdvancedFilterManager } from "./AdvancedFilter/advancedFilterManager";
import { BootgridState, GridView, ViewManagerView } from "./ViewManager/viewManagerTypes";
import { constructBootgridFormatters } from "./bootgridFormatters";
import { BootgridSettings } from "./bootgridSettings";
import { BootgridStateConstructor } from "./ViewManager/bootgridStateConstructor";
import { postValueChange } from "../wizard";
import { BootgridManager } from "./bootgridManager";
import { BootgridChangeType, ColumnFilter, DataType, Filter, PageState } from "./bootgridTypes";
import { ViewManager } from "./ViewManager/ViewManager";
import { isRecordPage } from "../Utils/isRecordPage";
import { deleteRecords } from "../record";
import { getActionFromUrl, ModalRenderedDetail } from "../initializer";
import { gridViewApi } from "./ViewManager/ViewApi/GridViewApi";
import postPageState from "./post-page-state";

const emptyBootgridData = {
    current: 1,
    rowCount: 10,
    rows: [],
    total: 0
};
export class Bootgrid {
    private onNewButtonClick = () => this.newRecordHandler();
    private onEditButtonClick = (e: Event) => {
        const editBtn = (e.currentTarget as HTMLButtonElement);
        editBtn.disabled = true;

        const rowId = editBtn.dataset.rowId ?? this.getSelectedRowIds()[0];
        this.editRecordHandler(rowId).then(() => {
            editBtn.disabled = false;
        });
    };
    private onDeleteButtonClick = async (e: Event) => {
        const currentTarget = (e.currentTarget as HTMLElement);
        if (e instanceof MouseEvent && (e.ctrlKey || e.shiftKey)) {
            return;
        }

        let selectedRows = this.getSelectedRowIds() as string[];
        const targetRowId = currentTarget.dataset.rowId;
        if (targetRowId) {
            if (selectedRows.includes(targetRowId)) {
                e.stopPropagation();            
            } else {
                selectedRows = [targetRowId];
            }
        }

        await this.deleteRecords(selectedRows);
    };        
    
    private onExportButtonClick = () => this.tableExport();

    private stateChangeHandler = async () => {
        if (this.isWizardPage()) return;
        let selectedViewId = this.viewManager.getSelectedViewId();
        if (!selectedViewId) return;
        const selectedView = this.views.find((view) => view.Id === selectedViewId);
        this.incrementLoading();
        if (!selectedView.Name.startsWith("luscinia-temp-view-")) {
            const tempViewName = `luscinia-temp-view-${selectedView.Id}`;
            const tempView = this.ConstructGridView(tempViewName);
            tempView.Id = await gridViewApi.saveView(tempView);
            await this.viewManager.refreshOptions();
            this.viewManager.setSelectedViewId(tempView.Id, false);

            this.views.map((view) => (view.IsActive = view.Id === tempView.Id));
            this.viewManager.updateButtonVisibilities(selectedView.ButtonVisibilities);
        } else {
            const tempView = this.ConstructGridView(selectedView.Name);
            await gridViewApi.updateView(selectedView.Id, tempView);
        }
        this.decrementLoading();
    }

    private async deleteRecords(recordIds: string[]) {
        var url = this.$bootgrid.data(luscinia.data.modalUrl);
        if (url === undefined || url === "") {
            url = this.$bootgrid.data(luscinia.data.baseUrl);
        }

        try {
            await deleteRecords(url, recordIds, () => (this.$bootgrid as any).bootgrid("reload"));
        } catch (error) {
            if (error instanceof Error) {
                luscinia.showNotification(luscinia.config.translations.captionNotification, error.message, "danger");
            } else {
                luscinia.showNotification(luscinia.config.translations.captionNotification, "An unknown error occurred while deleting the error", "danger");
            }
        }
    }

    public async deleteSelectedRowsHandler() {
        let selectedRows = this.getSelectedRowIds();
        
        if (selectedRows.length === 0) {
            luscinia.showNotification(
                luscinia.config.translations.captionNotification, 
                "Please select one or more records to delete.", 
                "warning"
            );
            return;
        }

        await this.deleteRecords(selectedRows);
    }

    public newRecordHandler() {
        const dataNewModalUrl = this.$bootgrid.data("new-record-modal-url");
        if (dataNewModalUrl) {
            luscinia.openCustomModal(dataNewModalUrl)
        }
        else {
            return luscinia.openNewRecordModal(this.$bootgrid, luscinia.config);
        }
    }

    public tableExport() {
        let menuItemId = this.$bootgrid.data(luscinia.data.menuItemId);
        let url = this.$bootgrid.data(luscinia.data.modalUrl);
        if (url === undefined || url === "") {
            url = this.$bootgrid.data(luscinia.data.baseUrl);
        }
        const filterColumnName = luscinia.data.filterColumnName;
        let parentBootgrid = $("." + luscinia.css.bootgrid + "[data-" + filterColumnName + "='']");
        let rows = parentBootgrid.find("tr." + luscinia.css.rowSelected);

        const downloadFromUrl = (exportUrl: string) => {
            fetch(exportUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(this.getFilter()),
            })
                .then((response) => {
                    if (response.ok) {
                        return response.blob();
                    } else {
                        throw new Error("Error downloading file");
                    }
                })
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "export.xlsx";
                    a.click();
                    URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    luscinia.showNotification("An error occurred", "An error occurred while downloading the file.", "danger");
                });
        };

        if ((this.$bootgrid as any).data(filterColumnName) === "") {
            const exportUrl = url + "/export/" + menuItemId + "?format=excel";
            downloadFromUrl(exportUrl);
        } else {
            const exportUrl = `${url}/export/${menuItemId}/${this.$bootgrid.data(filterColumnName)}/${$(rows[0]).data("row-id")}?format=excel`;
            downloadFromUrl(exportUrl);
        }
    }
    public getSelectedRowIds(): string[] {
        return (this.$bootgrid as any).bootgrid("getSelectedRowIds")
    }
    public editRecordHandler(recordId: string): JQuery.jqXHR {
        const menuItemId = this.$bootgrid.data(luscinia.data.menuItemId);
        const layerId = luscinia.getModalLayerCount();
        const modalIdentifier = "#editModal" + layerId;

        let url = this.$bootgrid.data(luscinia.data.modalUrl);
        if (url === undefined || url === "") {
            url = this.$bootgrid.data(luscinia.data.baseUrl);
        }

        url = url + "/modaledit?action=edit&id=" + recordId + "&bootgridId=bootGrid" + menuItemId + "&layerid=" + layerId;
        return $.get(url, (data) => {
            $("#cl-wrapper").append(data);
            luscinia.initModalDialog($(modalIdentifier + "Form"), modalIdentifier, luscinia.config, getActionFromUrl(url));
            $(modalIdentifier).modal("show");
            $(modalIdentifier + ' .modal-body :input:enabled:visible:first').focus();

            document.dispatchEvent(new CustomEvent<ModalRenderedDetail>('modalRendered', {
                detail: {
                    recordTableName: this.$bootgrid.get(0).dataset.lusciniaTablename,
                    modalIdentifier: modalIdentifier,
                }
            }));
        }).fail(function () {
            luscinia.showNotification(luscinia.config.translations.captionNotification, luscinia.config.translations.captionNotificationForbidden, "danger");
        })
    }

    viewManager: ViewManager;
    views: ViewManagerView[];

    advancedFilterManagerContainer: HTMLElement;
    advancedFilterManager: AdvancedFilterManager;

    id: string;
    bootgridManager: BootgridManager;
    bootgridData = luscinia.bootgrid.data;
    config: Config;
    bootgridSettings: BootgridSettings;
    bootgridState: BootgridState;
    $bootgrid: JQuery<HTMLTableElement>;
    tableName: string;
    //since menu item name is unique for each bootgrid in the page this will be used to save and retrieve available views and page view bootgrid view identification.
    menuItemName: string; 
    container: HTMLElement;

    previousRows: string[];
    previousSelectedRowIds: string[];

    constructor(config: Config, bootgrid: any, bootgridState?: BootgridState) {
        this.config = config;
        this.$bootgrid = bootgrid;
        this.id = bootgrid.get(0).id;
        this.tableName = bootgrid.data("luscinia-tablename");
        this.menuItemName = bootgrid.data("menu-item-name");
        this.bootgridSettings = new BootgridSettings(bootgrid, config);
        if (this.bootgridSettings.isParameterBootgrid) {
            throw new Error("You are using wrong initialization for this bootgrid use parameter bootgrid class instead");
        }
        this.bootgridState = bootgridState || this.bootgridSettings.bootgridState;
        this.container = bootgrid[0].closest(".table-container");
    }

    async init(bootgrid?: JQuery<HTMLTableElement>, pageState?: PageState, initialViewId: string = "default", loadDataOnInit: boolean = null): Promise<void> {
            this.incrementLoading();
            const preventInitialLoad =
                loadDataOnInit !== null
                    ? !loadDataOnInit
                    : !this.bootgridSettings.inRecordView && this.$bootgrid.data(luscinia.data.filterColumnName) !== "";
    
            this.$bootgrid = bootgrid || this.$bootgrid;
            this.applyBootgridStateBeforeInit();
            this.initJqueryBootgrid(pageState, false);
            this.addWizardBootgridHeader(this.$bootgrid);
            if (!this.$bootgrid.data("luscinia-advancedsearchdisabled")) {
                this.initAdvancedFilter();
            } else if (this.bootgridSettings.addColumnSearch) {
                this.initColumnSearches();
            }
            this.initGridViewManager(initialViewId);
            this.applyBootgridStateAfterInit();
            this.addEventListenersAfterInitialize();
    
            if (!preventInitialLoad) {
                await this.submitFilter();
            }
            this.decrementLoading();
    }
    private renderAdvancedFilterToggleButton() {
        const constructFilterToggleButton = (): HTMLButtonElement => {
            const actionButtonsContainer = this.container.querySelector(".actionBar-left") as HTMLElement;
            var btn = document.createElement("button");
            btn.classList.add("btn", "btn-outline-secondary", "text-dark", "d-flex", "gap-1", "align-items-center", "advanced-filter-toggle-button");
            if (this.bootgridState.AdvancedFilters) {
                btn.classList.add("active");
            }
            btn.setAttribute("type", "button");
            btn.setAttribute("title", "Toggle Filters");
            var icon = document.createElement("i");
            icon.classList.add("fa", "fa-filter");
            icon.ariaHidden = "true";
            btn.appendChild(icon);
            var iconText = document.createElement("span");
            iconText.classList.add("hidden-sm", "hidden-xs");
            iconText.innerHTML = this.config.translations.captionActionAdvancedFilters;
            btn.appendChild(iconText);
            actionButtonsContainer.appendChild(btn);

            return btn;
        };

        const toggleButton = constructFilterToggleButton();

        toggleButton.addEventListener("click", (e: MouseEvent) => {
            e.preventDefault();
            this.toggleAdvancedFilter();
        });
    }
    getCurrentPage(): string {
        return (this.$bootgrid as any).bootgrid("getCurrentPage");
    }
    private async initGridViewManager(initialViewId: string) {
        if (this.container === undefined || this.container === null) return;
        if (this.container.querySelector(".view-manager") !== null) return;
        if (this.isWizardPage()) return;

        this.viewManager = new ViewManager({
            addButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
                luscinia.showConfirmationDialogWithExtraInput(
                    "Add new view",
                    "you are about to add new view please provide a name for the view ",
                    async (inputValue) => {
                        const gridView = this.ConstructGridView(inputValue);
                        const savedViewId = await gridViewApi.saveView(gridView);
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId(savedViewId);
                    }
                );
            },
            saveButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
                this.incrementLoading();
                const viewId = viewManager.getSelectedViewId();
                let view = this.views.find((view) => view.Id === viewId);
                if (view) {
                    let isTempViewSave: boolean = false;
                    if (view.Name.startsWith("luscinia-temp-view-")) {
                        const originalViewId = view.Name.replace("luscinia-temp-view-", "");
                        view = this.views.find((view) => view.Id === originalViewId);
                        isTempViewSave = true;
                    }

                    luscinia.showConfirmationDialog("Save view", `your changes will be saved to ${view.Name}`, async () => {
                        const gridView = this.ConstructGridView(view.Name);
                        await gridViewApi.updateView(view.Id, gridView);

                        if (isTempViewSave) {
                            await viewManager.refreshOptions();
                            viewManager.setSelectedViewId(view.Id);
                        }
                    });
                }
                this.decrementLoading();
            },
            editButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
                this.incrementLoading();
                luscinia.showConfirmationDialogWithExtraInput("Rename view", "please provide a new name for the view ", async (inputValue) => {
                    const viewId = viewManager.getSelectedViewId();
                    if (viewId) {
                        try {
                            await gridViewApi.updateViewName(viewId, inputValue);
                            await viewManager.refreshOptions();
                            viewManager.setSelectedViewId(viewId);
                        } catch (error) {
                            console.error(error);
                        }
                    }
                });
                this.decrementLoading();
            },
            shareButtonHandler: (e, viewManager) => {
                this.incrementLoading();
                const viewId = viewManager.getSelectedViewId();
                if (viewId && viewId !== "default") {
                    const view = this.views.find((view) => view.Id === viewId);
                    const setViewPrivate = async () => {
                        await gridViewApi.setViewPrivate(viewId, !view.IsPrivate)
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId(viewId);
                    };
                    setViewPrivate();
                }
                this.decrementLoading();             
            },
            deleteButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
                this.incrementLoading();
                const viewId = viewManager.getSelectedViewId();
                if (viewId) {
                    luscinia.showConfirmationDialog("Delete view", "you are about to delete a view are you sure?", async () => {
                        await gridViewApi.deleteView(viewId);
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId("default");
                    });
                }
                this.decrementLoading();
            },
            select2ChangeHandler: async (viewManager: ViewManager) => {
                this.incrementLoading();
                const selectedViewId = viewManager.getSelectedViewId();
                if (!selectedViewId) return;

                this.views.map((view) => (view.IsActive = view.Id === selectedViewId));

                const selectedView = this.views.find((view) => view.Id === selectedViewId);
                viewManager.updateButtonVisibilities(selectedView.ButtonVisibilities);
                viewManager.setIsPrivate(selectedView.IsPrivate);

                if (selectedViewId != "default" && selectedViewId != null && selectedViewId != undefined) {
                    const view = await gridViewApi.getView(selectedViewId);
                    this.bootgridState = view.BootgridState;
                    await this.reRender();
                }
                if (selectedViewId === "default") {
                    const defaultView = await gridViewApi.getDefaultGridView(this.tableName);
                    this.bootgridState = defaultView.BootgridState;
                    await this.reRender();
                }
                this.decrementLoading();
            },
            getViews: async (viewManager: ViewManager) => {
                this.incrementLoading();
                const gridViews = await gridViewApi.getViews(this.tableName);

                const defaultView: ViewManagerView = {
                    Id: "default",
                    UserId: "default",
                    Name: "Default",
                    DisplayName: "Default",
                    IsActive: false,
                    IsVisible: true,
                    IsPrivate: true,
                    IsDefault: true,
                    ButtonVisibilities: {
                        ShowAddButton: true,
                        ShowDeleteButton: false,
                        ShowEditButton: false,
                        ShowSaveButton: false,
                        ShowShareButton: false,
                    },
                };

                gridViews.unshift(defaultView);

                gridViews.forEach((view) => {
                    if (view.Name.startsWith("luscinia-temp-view-")) {
                        const originalViewId = view.Name.replace("luscinia-temp-view-", "");
                        const originalViewName = gridViews.find((v) => v.Id === originalViewId)?.Name;
                        view.DisplayName = originalViewName + "*";
                        if (originalViewId === "default") {
                            view.IsDefault = true;
                            view.ButtonVisibilities = {
                                ShowAddButton: true,
                                ShowDeleteButton: false,
                                ShowEditButton: false,
                                ShowSaveButton: false,
                                ShowShareButton: false,
                            };
                        } else {
                            view.ButtonVisibilities = {
                                ShowAddButton: true,
                                ShowDeleteButton: false,
                                ShowEditButton: false,
                                ShowSaveButton: true,
                                ShowShareButton: false,
                            };
                        }
                    } else {
                        view.DisplayName = view.Name;
                        view.IsVisible = true;
                        if (!view.IsDefault) {
                            view.IsDefault = false;
                            view.ButtonVisibilities = {
                                ShowAddButton: true,
                                ShowDeleteButton: true,
                                ShowEditButton: true,
                                ShowSaveButton: true,
                                ShowShareButton: true,
                            };
                        }
                    }
                });

                const userId = this.$bootgrid.data('luscinia-userid');
                gridViews.map((view) => {
                    view.IsActive = view.Id === initialViewId;
                    view.ButtonVisibilities.ShowShareButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                    view.ButtonVisibilities.ShowDeleteButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                    view.ButtonVisibilities.ShowEditButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                    view.ButtonVisibilities.ShowSaveButton = view.UserId === userId && !(view.Name === "luscinia-temp-view-default");
                });

                this.views = gridViews;
                this.decrementLoading();
                return gridViews;
            },
        });

        const viewManagerContainer = document.createElement("div");
        viewManagerContainer.classList.add("view-manager-container");
        this.container.querySelector(".actionBar-right").appendChild(viewManagerContainer);
        this.viewManager.init(viewManagerContainer);
    }
    ConstructGridView(gridViewName: string): GridView {
        const bootgridProcessor = new BootgridStateConstructor(this);
        const gridView: GridView = {
            Id: EmptyGuid.value,
            UserId: EmptyGuid.value,
            ParentName: this.tableName,
            Name: gridViewName,
            IsPrivate: true,
            BootgridState: bootgridProcessor.constructBootgridState(),
        };

        return gridView;
    }
    getDisplayedColumns(): { columnId: string, dataType: DataType }[] {
        const thEls = Array.from(
            this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])')
        ) as HTMLElement[];
        return thEls.map((thEl) => {return {columnId: thEl.dataset.columnId, dataType: thEl.dataset.type as DataType}});
    }
    private applyBootgridStateBeforeInit() {
        this.applyVisibleColumns();
        this.applyColumnSearch();
        if (this.bootgridState.SortColumn !== null && this.bootgridState.SortColumn.ColumnDataId !== "") {
            this.applySortColumn();
        }
    }
    private applyBootgridStateAfterInit() {
        if (!this.$bootgrid.data("luscinia-advancedsearchdisabled")) {
            this.applyAdvancedFilterState();
        }
    }
    private applySortColumn() {
        this.resetSort();
        this.applySort();
    }
    private applySort() {
        this.$bootgrid.find("th").each((index, element) => {
            if (this.bootgridState.SortColumn.ColumnDataId === $(element).data("column-id")) {
                $(element).attr("data-order", this.bootgridState.SortColumn.SortDirection);
                return false;
            }
        });
    }
    private applyColumnSearch() {
        if (this.bootgridState.ColumnSearches === undefined || this.bootgridState.ColumnSearches.length === 0) {
            this.bootgridSettings.columnSearchValues = {};
            return;
        }

        let obj = {};
        this.bootgridState.ColumnSearches.forEach((columnSearch) => {
            obj[columnSearch.ColumnDataId] = columnSearch.SearchValue;
        });
        this.bootgridSettings.columnSearchValues = obj;
    }
    private initJqueryBootgrid(pageState: PageState, loadDataOnInit: boolean = null) {
        let isInitial = true;
        (this.$bootgrid as any)
            .on("loaded.rs.jquery.bootgrid", () => {
                this.addHeaderButtonEventListeners();
                this.addDeleteButtonEventListener();
                this.enableDisableEditAndDeleteButtonsBasedOnSelectedRow();
                if (!this.bootgridSettings.inRecordView && !this.isWizardPage()) {
                    if (isInitial && !this.bootgridSettings.allowMultiSelect) {
                        this.applyLastSelectedRow(pageState);
                        isInitial = false;
                    }
                }
                
                // Add open modal event listener to edit buttons
                const rows = this.$bootgrid.find("tbody > tr");
                rows.each((index, element) => {
                    const editBtn = element.querySelector(".command-edit") as HTMLButtonElement;
                    if (editBtn) {
                        editBtn.addEventListener("click", this.onEditButtonClick);
                    }
                }); 

                this.initInlineEditable();
                this.$bootgrid.trigger("onloaded", this.bootgridSettings);
            })
            .on("selected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                postPageState();
                if (this.hasPostValueChange()) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(this, rows, BootgridChangeType.select)
                        : null;
                    this.postValueChange(additionalFormData);
                }
            })
            .on("deselected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                postPageState();
                if (this.hasPostValueChange()) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(this, rows, BootgridChangeType.deselect)
                        : null;
                    this.postValueChange(additionalFormData);
                }
            })
            .on("click.rs.jquery.bootgrid", (columns: object[], row: object[]) => {
                this.refreshSubBootgrids();
            })
            .on("afterReloadWithData", (e: Event, bootgrid: any) => {
                if (this.hasPostValueChange()) {
                    if (this.selectedRowsChanged()) {
                        const additionalFormData = this.bootgridSettings.allowMultiSelect
                            ? this.bootgridManager.getPostValueChangeFormData(this, [], BootgridChangeType.search, bootgrid)
                            : null;
                        this.postValueChange(additionalFormData);
                    }
                }
            })
            .on("rowCountChange", async () => {
                await this.reloadTable();
                this.stateChangeHandler();
            })
            .on("pageChange", async () => {
                await this.reloadTable();
                this.stateChangeHandler();
            })
            .on("columnSearchCollapsedChanged", this.stateChangeHandler)
            .on("initialized.rs.jquery.bootgrid", () => {
                this.constructTableUrl();
                this.initColumnSearches();
                this.initSortColumn();
                this.initializeColumnDragAndDrop(this.$bootgrid[0]);
            })
            .on("headerRenderedAfterColumnSelectionChanged", () => {
                this.initializeColumnDragAndDrop(this.$bootgrid[0]);
                this.stateChangeHandler();
            })
            .on("stateChanged", this.stateChangeHandler)
            .bootgrid({
                searchSettings: { delay: 500 },
                selectedRowCount:
                    this.bootgridState.RowDisplayCount === null || isNaN(this.bootgridState.RowDisplayCount)
                        ? 10
                        : this.bootgridState.RowDisplayCount,
                current:
                    this.bootgridState.CurrentPage === null || isNaN(this.bootgridState.CurrentPage) ? 1 : this.bootgridState.CurrentPage,
                twinvisionColumnSearchCollapsed: this.bootgridState.IsColumnSearchCollapsed,
                twinvisionColumnSearchValues: this.bootgridSettings.columnSearchValues,
                rowCount: this.bootgridSettings.paging ? [10, 25, 50, -1] : -1,
                refreshIconVisible: this.bootgridSettings.showRefreshButton,
                columnSelectionVisible: this.bootgridSettings.showColumnSelection,
                selection: this.bootgridSettings.allowSelection,
                multiSelect: true,
                twinvisionColumnSearch: this.bootgridSettings.addColumnSearch,
                twinvisionColumnSearchExclusions: ["commands"],
                rowSelect: this.bootgridSettings.allowRowSelection,
                keepSelection: this.bootgridSettings.allowMultiSelect,
                ajax: this.bootgridSettings.ajax,
                alwaysHaveOneRowSelected: true,
                preventInitialLoad:
                    loadDataOnInit !== null
                        ? !loadDataOnInit
                        : !this.bootgridSettings.inRecordView && this.$bootgrid.data(luscinia.data.filterColumnName) !== "",
                post: () => {
                    let formData = new FormData();
                    if (this.$bootgrid.data("url").startsWith("/parameter")) {
                        formData = luscinia.serializeFormObject((this.$bootgrid as any).closest("form"));
                    }
                    formData.append("id", this.$bootgrid[0].id);
                    return luscinia.objectifyForm(formData);
                },
                url: () => {
                    return this.getUrl();
                },
                onColumnSelectionChanged: async () => {
                    this.incrementLoading();
                    const columnSearches = this.getColumnSearches();
                    const sortColumn = this.getColumnSort();
                    await this.reloadTable();
                    this.initColumnSearches(columnSearches);
                    this.initSortColumn(sortColumn);
                    this.decrementLoading();
                },
                onReload: () => this.reloadTable(),
                onRowClicked: () => this.refreshSubBootgrids(),
                css: this.constructCss(),
                formatters: constructBootgridFormatters(this.$bootgrid, this.bootgridSettings, this.config),
                templates: this.constructTemplates(),
                labels: this.constructLabels(),
            });
    }
    loadingCounter: number = 0;
    private incrementLoading() {
      this.loadingCounter++;
      if (this.loadingCounter === 1) {
          this.container.setAttribute("is-loading", "true");
      }
    }

    private decrementLoading() {
      this.loadingCounter = Math.max(0, this.loadingCounter - 1);
      if (this.loadingCounter === 0) {
          this.container.setAttribute("is-loading", "false");
      }
    }
    private async reloadTable() {
        this.incrementLoading();
        if (!this.bootgridSettings.allowMultiSelect) {
            const selectedRows: string[] = this.getSelectedRowIds();
            await this.submitFilter();
            const currentRows: string[] = (this.$bootgrid as any).bootgrid("getCurrentRowIds");
            if (selectedRows.length === 0 || !currentRows.some(item => selectedRows.includes(item))) {
                this.selectRowByIndex(0);
            }
            else {
                (this.$bootgrid as any).bootgrid("select", selectedRows);
            }
            this.refreshSubBootgrids();
        } else {
            await this.submitFilter();
            this.refreshSubBootgrids();
        }
        this.decrementLoading();
    }
    private initInlineEditable() {
        var inputList = document.querySelectorAll('input.inline-editable') as NodeListOf<HTMLInputElement>;

        inputList.forEach(input => {
            input.addEventListener('change', () => {
                $(input).attr('value', input.value);
                let rows = (this.$bootgrid as any).bootgrid('getCurrentRows') as object[];
                const rowId = $(input).closest("tr").attr('data-row-id');
                const keyColumnName = this.$bootgrid.attr('data-luscinia-keycolumnname');
                let changedRow = rows.filter(row => row[keyColumnName] == rowId);
                let changedRowIndex = rows.findIndex(row => row[keyColumnName] == rowId);
                const changedPropertyName = $(input).attr('name');
                if (input.value != "") {
                    changedRow[0][changedPropertyName] = input.value;
                } else {
                    changedRow[0][changedPropertyName] = "0";
                }
                rows[changedRowIndex] = changedRow[0];
                const additionalFormData = this.bootgridManager.getPostValueChangeFormData(this, changedRow, BootgridChangeType.update,null, changedPropertyName);
                this.postValueChange(additionalFormData);

            });
        });
    }

    private constructTableUrl() {
        if (this.isMainBootgrid() || isRecordPage()) return;
        
        const mainBootgrid = (this.getMainBootgrid() as any);
        const selectedRows: string[] = mainBootgrid.bootgrid("getSelectedRows");
        const mainBootgridIdentifier: string = mainBootgrid.bootgrid("getIdentifier");
        const baseUrl = this.$bootgrid.data(luscinia.data.baseUrl);
        const filterColumnName = this.$bootgrid.data(luscinia.data.filterColumnName);
        const parentFilterColumnName = this.$bootgrid.data(luscinia.data.parentFilterColumnName);
        const filterValue = selectedRows.map((row) => parentFilterColumnName ? row[parentFilterColumnName] : row[mainBootgridIdentifier]).join(","); 

        this.$bootgrid.get(0).dataset.url = `${baseUrl}/bootgrid/${filterColumnName}?filter-value=${encodeURIComponent(filterValue)}`;
    }
    private refreshSubBootgrids() {
        if (!this.isMainBootgrid()) {
            return;
        }
        const selectedRows: string[] = (this.$bootgrid as any).bootgrid("getSelectedRows");
        if (selectedRows.length > 0) {
            const mainBootgridIdentifier: string = (this.$bootgrid as any).bootgrid("getIdentifier");
            this.getSubBootgrids().each((index, element) => {
                const $currentBootgrid = $(element);
                const baseUrl = $currentBootgrid.data(luscinia.data.baseUrl);
                const filterColumnName = $currentBootgrid.data(luscinia.data.filterColumnName);
                const parentFilterColumnName = $currentBootgrid.data(luscinia.data.parentFilterColumnName);
                const filterValue = selectedRows.map((row) => parentFilterColumnName ? row[parentFilterColumnName] : row[mainBootgridIdentifier]).join(","); 
                element.dataset.url = `${baseUrl}/bootgrid/${filterColumnName}?filter-value=${encodeURIComponent(filterValue)}`;
    
                ($currentBootgrid as any).bootgrid("reload");
            });
        } else {
            this.getSubBootgrids().each((index, element) => {
                element.dataset.url = "";
                const $currentBootgrid = $(element);
                ($currentBootgrid as any).bootgrid("reload");
            });
        }
    }
    private getSubBootgrids() {
        return $(`.${luscinia.css.bootgrid}[data-luscinia-filtercolumnname]:not([data-luscinia-filtercolumnname=""])`);
    }
    private getMainBootgrid() {
        return $(`.${luscinia.css.bootgrid}[data-luscinia-filtercolumnname=""]`);
    }
    public isMainBootgrid() {
        return this.$bootgrid.data(luscinia.data.filterColumnName) === "";
    }
    initSortColumn(sortColumn: Record<string, string> = null) {
        const clearSortIcons = () => {
            const iconElements = this.container.querySelectorAll(
                'table > thead > tr > th[data-column-id]:not([data-column-id="commands"]) > a > .fa'
            ) as NodeListOf<HTMLElement>;
            iconElements.forEach((iconElement) => {
                iconElement.classList.remove("fa-chevron-down");
                iconElement.classList.remove("fa-chevron-up");
            });
        };

        const sortIconClickHandler = async (thEl: HTMLElement) => {
            toggleSortIcon(thEl);
            await this.reloadTable();
            this.stateChangeHandler();
        };

        const thEls = this.container.querySelectorAll(
            'table > thead > tr > th[data-column-id]:not([data-column-id="commands"])'
        ) as NodeListOf<HTMLElement>;
        thEls.forEach((thEl) => {
            if (sortColumn) {
                const sortColumnId = Object.keys(sortColumn)[0];
                const sortDirection = sortColumn[sortColumnId];

                if (thEl.getAttribute("data-column-id") === sortColumnId) {
                    clearSortIcons();
                    const iconElement = thEl.querySelector(".fa");
                    if (sortDirection === "asc") {
                        iconElement.classList.add("fa-chevron-up");
                    } else {
                        iconElement.classList.add("fa-chevron-down");
                    }
                }
            }

            const aEl = thEl.querySelector("a");
            if (aEl.dataset.sortInitialized !== "true") {
                aEl.dataset.sortInitialized = "true";
                aEl.addEventListener("click", () => {
                    sortIconClickHandler(thEl);
                });
            }
        });

        function toggleSortIcon(thEl: HTMLElement): "asc" | "desc" {
            const currentSort = getCurrentSort(thEl);
            clearSortIcons();
            if (!currentSort || currentSort === "asc") {
                const iconElement = thEl.querySelector(".fa");
                iconElement.classList.add("fa-chevron-down");
                return "desc";
            }
            if (currentSort === "desc") {
                const iconElement = thEl.querySelector(".fa");
                iconElement.classList.add("fa-chevron-up");
                return "asc";
            }

            function getCurrentSort(thEl: HTMLElement): "asc" | "desc" | null {
                const iconElement = thEl.querySelector(".fa");
                const currentSort = Array.from(iconElement.classList).find((className) => className.startsWith("fa-chevron"));
                if (!currentSort) return null;

                if (currentSort === "fa-chevron-down") {
                    return "desc";
                }

                return "asc";
            }
        }
    }
    initColumnSearches(columnFilters: ColumnFilter[] = null) {
        const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
        tableHeaders.forEach((header) => {
            const columnId = header.getAttribute("data-column-id");
            const inputEl = header.querySelector("input");
            if (inputEl) {
                const columnFilter = columnFilters?.find((filter) => filter.ColumnName === columnId);
                if (columnFilter) {
                    inputEl.value = columnFilter.Values[0];
                }
                const columnSearchHandler = this.debounce (async(e: KeyboardEvent) => {
                    if (
                        e.key === "Enter" ||
                        e.key === "Escape" ||
                        e.key === "Tab" ||
                        e.key === "Shift" ||
                        e.key === "Control" ||
                        e.key === "Alt" ||
                        e.key === "CapsLock"
                    ) {
                        this.decrementLoading();
                        return;
                    }
                    await this.reloadTable();
                    this.stateChangeHandler();
                    this.decrementLoading();
                }, 800);

                inputEl.removeEventListener("keyup", columnSearchHandler);
                inputEl.addEventListener("keyup", columnSearchHandler);

                inputEl.addEventListener("keypress", (event: KeyboardEvent) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                    }
                });
            }
        });

        const constructClearButton = () => {
            const actionButtonsContainer = this.container.querySelector(".actions.btn-group") as HTMLElement;
            
            if(actionButtonsContainer.querySelector(".clear-filters-button")) return;

            const btn = document.createElement("button");
            btn.classList.add("clear-filters-button");
            btn.addEventListener("click", (e: MouseEvent) => {
                e.preventDefault();
                this.clearColumnFilters();
            });
            btn.classList.add("btn", "btn-outline-secondary");
            btn.setAttribute("type", "button");


            const trashIcon = document.createElement("i");
            trashIcon.classList.add("fa", "fa-trash");

            btn.appendChild(trashIcon);
            // btn.appendChild(crossIcon);
            actionButtonsContainer.appendChild(btn);
        };

        constructClearButton();
    }
    debounceLoadingActive: boolean = false;
    private debounce(func: Function, wait: number) {
        let timeout: number | undefined;
        return (...args: any[]) => {
            if (!this.debounceLoadingActive) {
                this.debounceLoadingActive = true;
                this.incrementLoading();
            }
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
                func.apply(this, args);
                this.debounceLoadingActive = false;
            }, wait);
        };
    }
    private async clearColumnFilters() {
        const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
        tableHeaders.forEach((header) => {
            const inputEl = header.querySelector("input");
            if (inputEl) {
                inputEl.value = "";
                
            }
        });

        const generalSearchInput = (this.container?.querySelector("input.search-field") as HTMLInputElement);
        if (generalSearchInput) {
            generalSearchInput.value = "";
        }
        
        await this.reloadTable();
        this.stateChangeHandler();
    }
    initializeColumnDragAndDrop(table: HTMLTableElement) {
        let draggedElement: HTMLElement | null = null;
        const draggableElements = table.querySelectorAll('th[draggable="true"]:not(th[data-column-id="commands"])') as NodeListOf<HTMLElement>;
        draggableElements.forEach((draggableElement) => {
            draggableElement.addEventListener("dragstart", (event) => {
                draggedElement = draggableElement;
                draggedElement.style.opacity = "0.5";
            });

            draggableElement.addEventListener("dragover", (event) => {
                event.preventDefault();
                if (!draggedElement) return;
                const targetElement = event.target as Element;
                const targetParent = targetElement.closest("th");
                if (targetParent && !targetParent.classList.contains("drop-left") && !targetParent.classList.contains("drop-right")) {
                    const thRect = targetParent.getBoundingClientRect();
                    targetParent.classList.remove("drop-left", "drop-right");
                    targetParent.classList.add(isLeftHalf(event as DragEvent, thRect) ? "drop-left" : "drop-right");
                }
            });

            draggableElement.addEventListener("dragleave", (event) => {
                if (!draggedElement) return;
                const targetElement = event.target as Element;
                const targetParent = targetElement.closest("th");
                if (targetParent) {
                    targetParent.classList.remove("drop-left", "drop-right");
                }
            });

            draggableElement.addEventListener("drop", (event) => {
                event.preventDefault();
                if (!draggedElement) return;
                const targetElement = event.target as Element;
                const targetParent = targetElement.closest("th");

                if (targetParent && draggedElement !== targetParent) {
                    const currentDraggableElements = table.querySelectorAll(
                        'th[draggable="true"]:not(th[data-column-id="commands"])'
                    ) as NodeListOf<HTMLElement>;
                    const targetParentDisplayIndex = Array.from(currentDraggableElements).findIndex((el) => el === targetParent);
                    const draggedElementDisplayIndex = Array.from(currentDraggableElements).findIndex((el) => el === draggedElement);
                    const thRect = targetParent.getBoundingClientRect();
                    const dropPosition = isLeftHalf(event as DragEvent, thRect) ? "beforebegin" : "afterend";

                    if (
                        (targetParentDisplayIndex + 1 === draggedElementDisplayIndex && dropPosition === "afterend") ||
                        (targetParentDisplayIndex - 1 === draggedElementDisplayIndex && dropPosition === "beforebegin")
                    )
                        return;

                    targetParent.insertAdjacentElement(dropPosition, draggedElement);
                    const columnSettings = ($(table) as any).bootgrid("getColumnSettings");
                    const targetParentIndex = columnSettings.findIndex((column: any) => column.id === targetParent.dataset.columnId);
                    const draggedColumnIndex = columnSettings.findIndex((column: any) => column.id === draggedElement.dataset.columnId);
                    let targetIndex = targetParentIndex + (dropPosition === "beforebegin" ? -1 : 0);
                    if (draggedColumnIndex > targetIndex) {
                        targetIndex = targetIndex + 1;
                    }
                    moveElementInArray(columnSettings, draggedColumnIndex, targetIndex);
                    this.updateColumnOrder(columnSettings);
                    this.stateChangeHandler();
                }
            });

            draggableElement.addEventListener("dragend", (event) => {
                draggedElement.style.opacity = "1";
                draggedElement = null;
                draggableElements.forEach((el) => el.classList.remove("drop-left", "drop-right"));
            });
        });
        function moveElementInArray(array: any[], fromIndex: number, toIndex: number) {
            const elementToMove = array.splice(fromIndex, 1)[0];
            array.splice(toIndex, 0, elementToMove);
            return array;
        }
        function isLeftHalf(event: DragEvent, thRect: DOMRect) {
            return event.clientX - thRect.left < thRect.width / 2;
        }
    }
    private updateColumnOrder(columns: any[]) {
        (this.$bootgrid as any).bootgrid("updateColumnOrder", columns);
        this.reloadTable();
    }
    private selectedRowsChanged(): boolean {
        const selectedRowIds = this.getSelectedRowIds();
        if (this.previousSelectedRowIds === undefined) {
            this.previousSelectedRowIds = selectedRowIds;
            return false;
        }
        const valueChanged = !areArraysSame(this.previousSelectedRowIds, selectedRowIds);

        if (valueChanged) {
            this.previousSelectedRowIds = selectedRowIds;
        }
        return valueChanged;
    }
    private hasPostValueChange() {
        return this.$bootgrid.attr("data-luscinia-bootgrid-postvaluechange") === "true";
    }
    private postValueChange(additionalData: FormData = null) {
        let wizardForm = $(".block-wizard").find("form") as JQuery<HTMLFormElement>;
        let parameterName = $(".block-wizard").data("parameter");
        postValueChange.call(this.$bootgrid[0], wizardForm, parameterName, this.$bootgrid.attr("data-luscinia-field-name"), additionalData);
    }

    private addHeaderButtonEventListeners() {
        const newButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-new']");
        const editButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-edit']");
        const deleteButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-delete']");
        const exportButton = this.container?.querySelector("[data-" + luscinia.data.action + "='table-export']");

        newButton?.removeEventListener("click", this.onNewButtonClick);
        editButton?.removeEventListener("click", this.onEditButtonClick);
        deleteButton?.removeEventListener("click", this.onDeleteButtonClick);
        exportButton?.removeEventListener("click", this.onExportButtonClick);

        newButton?.addEventListener("click", this.onNewButtonClick);
        editButton?.addEventListener("click", this.onEditButtonClick);
        deleteButton?.addEventListener("click", this.onDeleteButtonClick);
        exportButton?.addEventListener("click", this.onExportButtonClick);
    }
    getSelectedRowCount(): string {
        let count = "-1";
        const rowCountDropdown = this.container.querySelector("#rowcount-dropdown") as HTMLElement;
        if (rowCountDropdown) {
            count = rowCountDropdown.querySelector("li.active > a").getAttribute("href").replace("#", "");
        }
        return count;
    }
    destroy() {
        (this.$bootgrid as any).bootgrid("destroy");
        this.advancedFilterManager.destroy();
    }
    async reRender() {
        this.destroy();
        await this.init($(`#${this.id}`), undefined, this.viewManager.getSelectedViewId(), true);
    }
    getFilter(): Filter {
        let columnFilters = this.getColumnSearches();
        if (!this.isWizardPage()) {
            columnFilters = columnFilters.concat(this.advancedFilterManager.getColumnFilters());
        }
        return {
            searchPhrase: (this.container?.querySelector("input.search-field") as HTMLInputElement)?.value ?? "",
            columnFilters: columnFilters,
            columnSort: this.getColumnSort(),
            rowCount: this.getSelectedRowCount(),
            currentPage: this.getCurrentPage(),
            displayedColumns: this.getDisplayedColumns(), // Used for export excel
        };
    }
    getColumnSearches(): ColumnFilter[] {
        let columnSearches: ColumnFilter[] = [];

        const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
        tableHeaders.forEach((header) => {
            const columnId = header.getAttribute("data-column-id");
            const inputEl = header.querySelector("input");

            if (inputEl && inputEl.value !== "") {
                let dataType: DataType = "String";

                const stringDateTypes = ["DateTime", "DateTimeOffset", "DateOnly"];
                const columnSearchDataType = (this.$bootgrid as any).bootgrid("getColumnSettings").find((column) => column.id === columnId)?.dataType;
                if (stringDateTypes.includes(columnSearchDataType)) {
                    dataType = "StringDate";
                }

                const columnFilter: ColumnFilter = {
                    ColumnName: columnId,
                    DataType: dataType,
                    FilterValue: "includes",
                    Values: [inputEl.value],
                };

                columnSearches.push(columnFilter);
            }
        });

        return columnSearches;
    }
    private getUrl(): string {
        const tableElement = this.container.querySelector(".luscinia-bootgrid") as HTMLTableElement;
        const filterColumnName = tableElement.getAttribute("data-luscinia-filtercolumnname");
        const parentFilterColumnName = tableElement.getAttribute("data-luscinia-parentfiltercolumnname");
        const baseUrl = tableElement.getAttribute("data-base-url");

        if (this.isWizardPage()) {
            return baseUrl + "/bootgrid";
        }

        if (isRecordPage() && filterColumnName) {
            const recordForm = document.getElementById("editModal0Form");
            let parentPropertyId = recordForm.dataset.lusciniaRecordId;
            const parentFilterElement = recordForm.querySelector(`[name='${parentFilterColumnName}']`) as HTMLInputElement;
            if (parentFilterElement) {
                parentPropertyId = parentFilterElement.value;
            }

            return `${tableElement.dataset.url}/${tableElement.dataset.lusciniaFiltercolumnname}?filter-value=${encodeURIComponent(parentPropertyId)}`;
        }

        if (filterColumnName !== "") {
            return tableElement.dataset.url;
        } else {
            return baseUrl + "/bootgrid";
        }
    }
    private async submitFilter(): Promise<void> {
        let url = this.getUrl();
        if (url === "") {
            await (this.$bootgrid as any).bootgrid("reloadWithData", emptyBootgridData);
            return;
        }

        let filter = this.getFilter();
        let data: any = JSON.stringify(filter);
        let isFormData = false;

        if (this.isWizardPage()) {
            const formElement = luscinia.getActiveForm();
            data = luscinia.serializeFormObject(formElement);
            data.append("filters", JSON.stringify(filter.columnFilters));
            data.append("rowCount", this.getSelectedRowCount());
            data.append("columnSort", JSON.stringify(filter.columnSort));
            data.append("current", this.getCurrentPage());
            isFormData = true;
        }

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: isFormData ? {} : { "Content-Type": "application/json" },
                body: data,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            await (this.$bootgrid as any).bootgrid("reloadWithData", responseData);
        } catch (error) {
            console.error("Something went wrong while filtering the data.", error);
        }
    }
    private addWizardBootgridHeader(bootgrid: JQuery<HTMLElement>) {
        if ((bootgrid.data("url") as string).includes(".Wizards.") && $("#bootgridHeader").length === 0) {
            const bootgridHeader = this.tableName;
            let h2 = document.createElement("h2");
            h2.id = "bootgridHeader";
            let text = document.createTextNode(bootgridHeader);
            h2.appendChild(text);
            bootgrid[0].previousSibling.insertBefore(h2, bootgrid[0].previousSibling.firstChild);
        }
    }
    private initAdvancedFilter() {
        if (!this.container.querySelector(".advanced-filter-toggle-button")) {
            this.renderAdvancedFilterToggleButton();
        }
        if (this.$bootgrid.parent().find("#advanced-search").length === 0) {
            this.advancedFilterManagerContainer = document.createElement("div");
            this.advancedFilterManagerContainer.classList.add("bootgrid-header", "mx-3", "bootgrid-advanced-search");

            this.container.querySelector(".bootgrid-header").insertAdjacentElement("afterend", this.advancedFilterManagerContainer);
            this.advancedFilterManager = new AdvancedFilterManager(this.advancedFilterManagerContainer, {
                columns: (this.$bootgrid as any).bootgrid("getColumnSettings"),
                filters: this.bootgridState.AdvancedFilters,
                onSubmit: () => this.reloadTable(),
                onStateChange: () => this.stateChangeHandler(),
            });
        }
        this.advancedFilterManager.render();
    }
    private applyAdvancedFilterState() {
        this.setAdvancedFilterVisibility();
    }
    private setAdvancedFilterVisibility() {
        const isCollapsed = (this.bootgridState.IsAdvancedSearchCollapsed ?? false) && !(this.bootgridState.AdvancedFilters?.length > 0);
        if (isCollapsed !== this.advancedFilterManagerContainer.classList.contains("d-none")) {
            this.toggleAdvancedFilter();
        }
    }
    private toggleAdvancedFilter() {
        this.advancedFilterManagerContainer.classList.toggle("d-none");
        this.container.querySelector(".advanced-filter-toggle-button").classList.toggle("active");
    }
    private applyVisibleColumns() {
        if (this.bootgridState.VisibleColumnIds?.length > 0) {
            this.resetVisibility();
            this.applyVisibility();
            this.orderByVisibility();
        }
    }
    private orderByVisibility() {
        const columnIdOrder = this.bootgridState.VisibleColumnIds;
        let lastMovedElement = null;

        columnIdOrder.forEach((columnId, orderIndex) => {
            this.$bootgrid.find("th").each((index, element) => {
                const currentColumnId = $(element).data("column-id");

                if (currentColumnId === columnId) {
                    const $elementToMove = $(element).detach();

                    if (lastMovedElement) {
                        $(lastMovedElement).after($elementToMove);
                    } else {
                        this.$bootgrid.find("tr").prepend($elementToMove);
                    }

                    lastMovedElement = $elementToMove;
                }
            });
        });

        if (this.$bootgrid.find('th[data-column-id="commands"]').length > 0) {
            this.$bootgrid.find("tr").prepend(this.$bootgrid.find('th[data-column-id="commands"]'));
        }
    }
    private applyVisibility() {
        this.$bootgrid.find("th").each((index, element) => {
            if (this.bootgridState.VisibleColumnIds.includes($(element).data("column-id"))) {
                $(element).attr("data-visible", "true");
            }
        });
    }
    private resetSort() {
        this.$bootgrid.find("th").each((index, element) => {
            $(element).removeAttr("data-order");
        });
    }
    private resetVisibility() {
        this.$bootgrid.find("th").each((index, element) => {
            const $th = $(element);
            if (typeof $th.attr("data-visible") !== "undefined") {
                $th.attr("data-visible", "false");
            }
        });
    }
    private async applyLastSelectedRow(pageState: PageState) {
        if (pageState === undefined || pageState === null || pageState.selectedRows === null) {
            this.selectRowByIndex(0);
            this.refreshSubBootgrids();
            return;
        }
        const selectedRowId = pageState.selectedRows[this.menuItemName];
        if (selectedRowId === undefined || selectedRowId === null || selectedRowId === "" || !this.rowAvailable(selectedRowId)) {
            this.selectRowByIndex(0);
        } else {
            (this.$bootgrid as any).bootgrid("select", [selectedRowId]);
        }
        this.refreshSubBootgrids();
    }
    private rowAvailable(rowId: string): boolean {
        return this.$bootgrid.find(`tr[data-row-id='${rowId}']`).length > 0;
    }
    public selectRowByIndex(index: number) {
        const firstRowId = this.$bootgrid.find("tbody > tr").eq(index).data("row-id");
        if (firstRowId === undefined || firstRowId === null || firstRowId === "") return false;
        (this.$bootgrid as any).bootgrid("select", [firstRowId]);
    }
    private enableDisableEditAndDeleteButtonsBasedOnSelectedRow() {
        let selectedRow = this.$bootgrid.find("tr." + luscinia.css.rowSelected);
        var id = selectedRow.data("row-id");
        if (id === undefined || id === "") {
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-edit']`)
                .attr("disabled", "");
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-delete']`)
                .attr("disabled", "");
        } else {
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-edit']`)
                .removeAttr("disabled");
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-delete']`)
                .removeAttr("disabled");
        }
        if (!this.bootgridSettings.showHeaderInfoBar) {
            this.$bootgrid.closest(".form-group").find(".bootgrid-header").hide();
        }
        if (!this.bootgridSettings.showFooterInfoBar) {
            this.$bootgrid.closest(".form-group").find(".bootgrid-footer").hide();
        }
    }
    private constructCss() {
        return {
            search: "search form-group pull-left",
            selected: luscinia.css.rowHighlight,
            selectCell: "select-cell" + (this.bootgridSettings.showCheckBox ? "" : " hidden"),
            infos: "infos" + (this.bootgridSettings.showFooterInfoBar ? "" : " hidden"),
            iconColumnSearchToggleCollapsed: "fa fa-search-plus",
            iconColumnSearchToggleUncollapsed: "fa fa-search-minus",
        };
    }
    private constructTemplates() {
        return {
            header: this.bootgridSettings.showActionHeader
                ? `<div id='{{ctx.id}}' class='{{css.header}}'>
                        <div class='actionBar-left d-flex gap-1'>
                            ${this.bootgridSettings.buttonHeaderNew}
                            ${this.bootgridSettings.buttonHeaderEdit}
                            ${this.bootgridSettings.buttonHeaderDelete} 
                            ${this.bootgridSettings.buttonHeaderExport}
                        </div>
                        <div class="actionBar-right d-flex gap-1"> 
                            <p class='{{css.search}}'></p>
                            <p class='{{css.actions}}'></p>
                        </div>
                    </div>`
                : "",
            actionDropDownCheckboxItem:
                `<li>
                    <label class="{{css.dropDownItem}}">
                        <input type="checkbox" value="1" class="{{css.dropDownItemCheckbox}}" {{ctx.checked}} />
                        {{ctx.label}}
                    </label>
                </li>`,
            search: this.bootgridSettings.showSearchInput
                ? `<div class="{{css.search}}">
                        <div class="input-group">
                            <span class="input-group-text">
                                <i class="{{css.iconSearch}}"></i>
                            </span>
                            <input type="text" class="{{css.searchField}}" placeholder="{{lbl.search}}" />
                        </div>
                   </div>`
                : "",
            columnSearchToggle:
            `<button type="button" class="{{css.columnSearchToggle}}">
                <i class="{{css.iconColumnSearchToggle}}"></i>
             </button>`,
        };
    }
    private constructLabels() {
        return {
            all: this.config.translations.bootgridAll,
            infos: this.config.translations.bootgridInfos,
            loading: this.config.translations.bootgridLoading,
            noResults: this.config.translations.bootgridNoResults,
            refresh: this.config.translations.bootgridRefresh,
            search: this.config.translations.bootgridSearch,
        };
    }
    private addDeleteButtonEventListener() {
        this.$bootgrid
            .get(0)
            .querySelectorAll(".command-delete")
            .forEach(btn => btn.addEventListener("click", this.onDeleteButtonClick));
    }
    private addEventListenersAfterInitialize() {
        const tbody = this.$bootgrid.find("tbody");
        const handleDoubleClick = () => {
            if (tbody.find("tr .no-results").length > 0) return;
            tbody.off("dblclick");

            this.editRecordHandler(this.getSelectedRowIds()[0]).then(() => {
                setTimeout(() => {
                    tbody.on("dblclick", handleDoubleClick);
                }, 500);
            });
        };

        tbody.on("dblclick", handleDoubleClick);
    }
    private isWizardPage(): boolean {
        return $("#wizardcontainer").length > 0;
    }    
    getColumnSort(): Record<string, "asc" | "desc"> {
        const thEls = this.container.querySelectorAll(
            'table > thead > tr > th[data-column-id]:not([data-column-id="commands"])'
        ) as NodeListOf<HTMLElement>;
        let columnSort: Record<string, "asc" | "desc"> = {};
        for (let i = 0; i < thEls.length; i++) {
            const thEl = thEls[i];
            const iconEl = thEl.querySelector("i.fa");
            if (iconEl.classList.contains("fa-chevron-up") || iconEl.classList.contains("fa-chevron-down")) {
                const columnId = thEl.getAttribute("data-column-id");
                const order = iconEl.classList.contains("fa-chevron-up") ? "asc" : "desc";
                columnSort[columnId] = order;
                return columnSort;
            }
        }
        return columnSort;
    }
}

const areArraysSame = (arr1: string[], arr2: string[]): boolean => {
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    return set1.size === set2.size && [...set1].every((item) => set2.has(item));
};